/**
 * Platform settings.
 *
 * The timing section deliberately leads with ONE question — how fast should
 * revoking a licence take effect — instead of the two raw clocks that decide
 * it. An operator pressing Revoke wants an outcome, not a check-in interval
 * and a grace period they have to add together, in seconds, while remembering
 * that one of them has a 60s floor and the other does not.
 *
 * The raw fields are still there under Advanced, because a fleet-wide check-in
 * interval is a real thing to tune on its own.
 */
import { type JSX, useEffect, useId, useMemo, useState } from 'react';
import { AdminApiClient } from '../api';
import { failureFrom, userMessageFor, type Failure } from '../lib/api-state';
import { usePermissions } from '../lib/permissions';
import { FetchState } from '../components/ui/FetchState';
import { useSession } from '../session';
import { Button } from '../components/ui/Button';

/** The engine clamps with Math.max(60, ...), so anything lower would display one thing and do another. */
const MIN_INTERVAL = 60;

interface Preset {
  id: string;
  label: string;
  detail: string;
  interval: number | null;
  grace: number | null;
}

const PRESETS: Preset[] = [
  {
    id: 'immediate',
    label: 'Immediately',
    detail: 'Engine stops within about a minute. Every engine checks in each minute.',
    interval: 60,
    grace: 0,
  },
  {
    id: 'quarter',
    label: 'Within 15 minutes',
    detail: 'A good balance — quick enough to act on, light on fleet traffic.',
    interval: 900,
    grace: 0,
  },
  {
    id: 'hour',
    label: 'Within an hour',
    detail: 'Engines check in hourly and stop as soon as they hear.',
    interval: 3600,
    grace: 0,
  },
  {
    id: 'default',
    label: 'Slowly (default)',
    detail: 'Engines check in every 6 hours, then keep running a further 24 — up to 30 hours in total.',
    interval: null,
    grace: null,
  },
];

const BUILTIN = { interval: 21600, grace: 86400 };

/** Worst case from the two clocks: time to hear about it, plus time to act. */
function worstCase(interval: number | null, grace: number | null): string {
  const i = interval ?? BUILTIN.interval;
  const g = grace ?? BUILTIN.grace;
  const secs = i + g;
  if (secs < 120) return 'about a minute';
  if (secs < 3600) return `about ${Math.round(secs / 60)} minutes`;
  if (secs < 86400) return `about ${Math.round(secs / 3600)} hours`;
  return `about ${Math.round(secs / 3600)} hours (${(secs / 86400).toFixed(1)} days)`;
}

function matchPreset(interval: number | null, grace: number | null): string {
  const hit = PRESETS.find((p) => p.interval === interval && p.grace === grace);
  return hit ? hit.id : 'custom';
}

export function SettingsPage(): JSX.Element {
  const session = useSession();
  const api = useMemo(
    () => new AdminApiClient({
      baseUrl: import.meta.env.VITE_LICENSE_SERVER_URL ?? '',
      getToken: () => session.state.token,
    }),
    [session.state.token],
  );

  const [interval, setInterval_] = useState<number | null>(null);
  const [grace, setGrace] = useState<number | null>(null);
  const [advanced, setAdvanced] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loadErr, setLoadErr] = useState<Failure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const perms = usePermissions();
  // GET and PUT /internal/settings/timing are both owner/admin only.
  const canUse = perms.canReadAdminData;

  useEffect(() => {
    if (!canUse) { setLoading(false); return undefined; }
    let live = true;
    setLoading(true); setLoadErr(null);
    api.platformTiming()
      .then((d) => {
        if (!live) return;
        setInterval_(d.default_heartbeat_interval_seconds);
        setGrace(d.default_revoke_grace_seconds);
        setAdvanced(matchPreset(d.default_heartbeat_interval_seconds, d.default_revoke_grace_seconds) === 'custom');
      })
      .catch((e: unknown) => { if (live) setLoadErr(failureFrom(e, 'the platform settings')); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [api, canUse, reloadKey]);

  const selected = matchPreset(interval, grace);

  const save = async (): Promise<void> => {
    if (interval !== null && interval < MIN_INTERVAL) {
      setErr(`Check-in interval must be at least ${MIN_INTERVAL} seconds. Only the grace may be 0.`);
      return;
    }
    setBusy(true); setMsg(null); setErr(null);
    try {
      const r = await api.updatePlatformTiming({
        default_heartbeat_interval_seconds: interval,
        default_revoke_grace_seconds: grace,
        reason: 'changed from Settings',
      });
      setMsg(r.note);
    } catch (e) {
      setErr(`Could not save: ${userMessageFor(e, 'the settings')}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <h1 style={{ margin: '0 0 4px' }}>Settings</h1>
      <p style={{ color: 'var(--text-muted)', margin: '0 0 24px' }}>
        Platform-wide defaults. These apply to every licence that has no setting of its own.
      </p>

      <section style={{
        border: '1px solid var(--border)', borderRadius: 6, padding: 20,
        background: 'var(--surface, transparent)',
      }}>
        <h2 style={{ margin: '0 0 4px', fontSize: '0.9rem' }}>When you revoke a licence, how fast should it take effect?</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', margin: '0 0 16px' }}>
          Engines are told at their next check-in, so the answer is really “how often do engines check in”.
        </p>

        {!canUse ? (
          <FetchState
            error={{
              kind: 'no_access', retryable: false,
              message: perms.role === 'owner' || perms.role === 'admin'
                ? 'Finish two-factor sign-in to view platform settings.'
                : 'Platform settings are available to Owner and Admin accounts.',
            }}
          />
        ) : loadErr ? (
          <FetchState error={loadErr} onRetry={() => setReloadKey((k) => k + 1)} />
        ) : loading ? <p role="status" className="muted">Loading…</p> : (
          <>
            <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
              {PRESETS.map((p) => (
                <label
                  key={p.id}
                  style={{
                    display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer',
                    border: `1px solid ${selected === p.id ? 'var(--accent, #6366f1)' : 'var(--border)'}`,
                    borderRadius: 6, padding: '10px 12px',
                  }}
                >
                  <input
                    type="radio" name="revoke-speed" checked={selected === p.id} disabled={busy}
                    onChange={() => { setInterval_(p.interval); setGrace(p.grace); }}
                    style={{ marginTop: 3 }}
                  />
                  <span>
                    <span style={{ fontWeight: 600 }}>{p.label}</span>
                    <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                      {p.detail}
                    </span>
                  </span>
                </label>
              ))}
              {selected === 'custom' && (
                <div style={{
                  border: '1px solid var(--accent, #6366f1)', borderRadius: 6, padding: '10px 12px',
                  fontSize: '0.8rem',
                }}>
                  <strong>Custom</strong>
                  <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                    Set below under Advanced.
                  </span>
                </div>
              )}
            </div>

            <p style={{
              fontSize: '0.8rem', margin: '0 0 16px', padding: '8px 12px',
              borderLeft: '3px solid var(--border)', color: 'var(--text-muted)',
            }}>
              A revoked engine will stop <strong>{worstCase(interval, grace)}</strong> after you press Revoke.
            </p>

            <button
              type="button"
              onClick={() => setAdvanced((v) => !v)}
              style={{
                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                color: 'var(--text-muted)', fontSize: '0.78rem', textDecoration: 'underline',
              }}
            >
              {advanced ? 'Hide advanced' : 'Advanced — set the two clocks directly'}
            </button>

            {advanced && (
              <div style={{ marginTop: 14 }}>
                <Field
                  label="Check-in interval (seconds)"
                  placeholder={`${BUILTIN.interval} — default (6h)`}
                  value={interval === null ? '' : String(interval)}
                  onChange={(v) => setInterval_(v.trim() === '' ? null : Number(v))}
                  disabled={busy} min={MIN_INTERVAL}
                  help={'How often every engine contacts the cloud. This sets how long ANY change takes '
                      + 'to reach it — revoke, suspend, resume, plan change. Minimum 60. Empty = default.'}
                />
                <Field
                  label="Revoke grace (seconds)"
                  placeholder={`${BUILTIN.grace} — default (24h)`}
                  value={grace === null ? '' : String(grace)}
                  onChange={(v) => setGrace(v.trim() === '' ? null : Number(v))}
                  disabled={busy} min={0}
                  help={'After an engine learns it was revoked, how long it keeps running before it stops. '
                      + '0 means it stops straight away. This is the only field that may be 0. Empty = default.'}
                />
              </div>
            )}

            <div style={{
              borderLeft: '3px solid var(--border)', padding: '8px 12px', margin: '16px 0',
              color: 'var(--text-muted)', fontSize: '0.78rem',
            }}>
              <strong>A single licence can override this.</strong> Open a licence and use its Timing
              card to give one customer different behaviour.
            </div>

            <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', margin: '0 0 14px' }}>
              Revoking does not delete anything: sites, databases and mail keep serving until the
              engine stops, and the data is untouched. What stops is the control plane, so
              certificates stop renewing. Suspend is the reversible option; revoke has no undo.
            </p>

            <Button onClick={() => void save()} disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
            {msg && <p role="status" style={{ fontSize: '0.78rem', marginTop: 10 }}>{msg}</p>}
            {err && <p className="err" role="alert" style={{ fontSize: '0.78rem', marginTop: 10 }}>{err}</p>}
          </>
        )}
      </section>
    </div>
  );
}

function Field({ label, value, onChange, help, placeholder, disabled, min }: {
  label: string; value: string; onChange: (v: string) => void;
  help: string; placeholder: string; disabled?: boolean; min: number;
}): JSX.Element {
  const uid = useId();
  return (
    <div style={{ marginBottom: 16 }}>
      <label htmlFor={uid} style={{ display: 'block', fontSize: '0.8125rem', marginBottom: 4 }}>{label}</label>
      <input
        id={uid}
        type="number" min={min} value={value} placeholder={placeholder} disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: '100%', padding: '7px 9px' }}
      />
      <p style={{ color: 'var(--text-muted)', fontSize: '0.72rem', margin: '5px 0 0', lineHeight: 1.5 }}>
        {help}
      </p>
    </div>
  );
}
